import { LoadingButton } from "@mui/lab";
import { Card, CardContent, CardHeader, Divider, Grid, TextField, Typography } from "@mui/material";
import { get, isString } from "lodash";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import OrderService from "src/_services/orderService";
import SearchIcon from '@mui/icons-material/Search';
import { MapItTextField } from "src/components/common";
import { MapitShipCitiesAutocomplete } from "src/_modules/MapitShip/components";

const BasicFields = [
    { name: "city", label: "City", size: "small", required: true },
    { name: "area", label: "Neighbourhood", size: "small", required: false },
    { name: "street", label: "Street", size: "small", required: false },
    { name: "houseNumber", label: "House Number", size: "small", required: false },
    { name: "floor", label: "Floor", size: "small", required: false },
    { name: "flat", label: "Flat", size: "small", required: false },
    { name: "landmark", label: "Notes", size: "small", required: false }
]

export default function AddressDetails({
    address,
    validationErrors,
    handleChange,
    CustomFields,
    nationalAddressRequired = false,
    cardTitle = "Address Info",
    mapitShip = false,
}) {
    const { t } = useTranslation();
    const [nationalAddress, setNationalAddress] = useState('')
    const [loading, setLoading] = useState(false)
    const [isSuccess, setIsSuccess] = useState(false)
    const [error, setError] = useState(null)
    const [validateNationalNumber, setValidateNationalNumber] = useState(null)

    const Fields = useMemo(() => {
        return CustomFields ?? BasicFields
    }, [CustomFields])

    const getAddressFromNationalAddress = async () => {
        try {
            setIsSuccess(false)
            setError(null)
            setValidateNationalNumber(null)
            setLoading(true)
            const valid = /^[A-Z]{4}[0-9]{4}/.test(nationalAddress)
            if (!valid || nationalAddress.length !== 8) {
                setValidateNationalNumber('Please make sure you entered a valid national number')
                return
            }
            let response = await OrderService.getAddressFromNationalAddress({ nationalAddress })
            let data = response.data.body
            handleChange({ target: { name: "address", value: data?.address } })
            handleChange({ target: { name: "location.coordinates", value: data?.location.map(coord => Number(coord)) } })
        } catch (err) {
            let message = err?.response?.data?.message ?? 'Something went wrong please try again'
            setError(message)
        } finally {
            setLoading(false)
        }
    }

    return (
        <Card>
            <CardHeader
                title={t(cardTitle)}
                subheader={isString(validationErrors) ? validationErrors : ""}
                subheaderTypographyProps={{ color: isString(validationErrors) ? "error" : '' }}
            />
            {
                nationalAddressRequired && <>
                    <Divider />
                    {
                        (isSuccess || !!error) &&
                        <CardContent>
                            {isSuccess && <Typography color="green.main">{t("Done successfully")}</Typography>}
                            {!!error && <Typography color="error">{t(error)}</Typography>}
                        </CardContent>
                    }
                    <CardContent>
                        <Grid container spacing={3} justifyContent="space-between">
                            <Grid item xs={8} md={10}>
                                <TextField
                                    fullWidth
                                    label={t("Short Address Code")}
                                    placeholder={t("Example (RRRR1234)")}
                                    size="small"
                                    name="nationalAddress"
                                    value={nationalAddress}
                                    onChange={(event) => setNationalAddress(event.target.value.toUpperCase())}
                                    error={!!validateNationalNumber}
                                    helperText={t(validateNationalNumber)}
                                />
                            </Grid>
                            <Grid item xs={4} md={2} sx={{ textAlign: "end" }}>
                                <LoadingButton endIcon={<SearchIcon />} onClick={getAddressFromNationalAddress} loading={loading} sx={{ textTransform: 'none' }} variant='contained'>
                                    {t("Search")}
                                </LoadingButton>
                            </Grid>
                        </Grid>
                    </CardContent>
                </>
            }
            <Divider />
            <CardContent>
                <Grid container spacing={3}>
                    {
                        Fields.map(field => (
                            <Grid item xs={12} md={6} key={field?.name}>
                                {
                                    (mapitShip && field?.name === "city") ?
                                        <MapitShipCitiesAutocomplete
                                            name={field?.name}
                                            value={get(address, field?.name)}
                                            onChange={handleChange}
                                            error={get(validationErrors, field?.name)}
                                        />
                                        :
                                        <MapItTextField
                                            label={t(field?.label)}
                                            size={field?.size}
                                            name={field?.name}
                                            value={get(address, field?.name) ?? ""}
                                            handleChange={handleChange}
                                            error={get(validationErrors, field?.name)}
                                            required={field?.required}
                                        />
                                }
                            </Grid>
                        ))
                    }
                </Grid>
            </CardContent>
        </Card>
    )
}
